// File: src/components/EventsFiltersAndHeadline/SortBy.jsx
import React, { useRef, useEffect, useMemo } from 'react';
import { FaCheck, FaStar } from 'react-icons/fa6';
import {
  RiSortAsc,
  RiSortDesc,
  RiSortAlphabetAsc,
  RiSortAlphabetDesc,
} from 'react-icons/ri';

const isPWA = () =>
  (window.matchMedia?.('(display-mode: standalone)').matches) ||
  window.navigator.standalone === true;

export default function SortBy({
  sortType,
  setSortType,
  sortOpen,
  setSortOpen,
}) {
  // Hooks must be unconditional
  const listRef = useRef(null);

    const overlayLockRef = useRef({ didLock: false, y: 0, prevTheme: '' });

  // ESC to close (guarded by sortOpen inside the effect)
  useEffect(() => {
    if (!sortOpen) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setSortOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sortOpen, setSortOpen]);

  // Optional: focus selected (or first) item when the sheet opens
  useEffect(() => {
    if (!sortOpen) return;
    const node = listRef.current;
    if (!node) return;
    // focus after render
    const id = requestAnimationFrame(() => {
      const selected =
        node.querySelector('[aria-selected="true"]') ||
        node.querySelector('[data-opt="1"]');
      selected?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [sortOpen, sortType]);


  // ✅ Freeze background scroll + notch color while open (matches TourBottomSheet behavior)
 useEffect(() => {
  const meta = document.querySelector('meta[name="theme-color"]');
  const prevTheme = meta?.content;

  if (sortOpen) {
    const alreadyLocked =
      document.body.classList.contains('overlay-active') ||
      document.body.style.position === 'fixed';

    overlayLockRef.current.didLock = !alreadyLocked;

    // notch/status bar
    overlayLockRef.current.prevTheme = prevTheme || '#ffffff';
    if (meta) meta.content = '#999998';

    // Freeze scroll at current position
    if (!alreadyLocked) {
      const y = window.scrollY || window.pageYOffset || 0;
      overlayLockRef.current.y = y;

      document.body.style.top = `-${y}px`;
      document.body.classList.add('overlay-active');
    }
  }

  return () => {
    // restore notch
    if (meta) meta.content = overlayLockRef.current.prevTheme || '#ffffff';

    // restore scroll only if we locked it
    if (overlayLockRef.current.didLock) {
      const y = overlayLockRef.current.y || 0;

      document.body.classList.remove('overlay-active');
      document.body.style.top = '';
      window.scrollTo(0, y);
    }
  };
}, [sortOpen]);



  const sortOptions = useMemo(() => ([
    { label: 'Relevance',            value: 'relevance', shortLabel: 'Relevance', icon: <FaStar style={{ fontSize: 14 }} /> },
    { label: 'Date: Earliest First', value: 'dateAsc',   shortLabel: 'Date ↑',    icon: <RiSortAsc style={{ fontSize: 14 }} /> },
    { label: 'Date: Latest First',   value: 'dateDesc',  shortLabel: 'Date ↓',    icon: <RiSortDesc style={{ fontSize: 14 }} /> },
    { label: 'Name: A–Z',            value: 'nameAsc',   shortLabel: 'Name A–Z',  icon: <RiSortAlphabetAsc style={{ fontSize: 14 }} /> },
    { label: 'Name: Z–A',            value: 'nameDesc',  shortLabel: 'Name Z–A',  icon: <RiSortAlphabetDesc style={{ fontSize: 14 }} /> },
  ]), []);

  const applySort = (value) => {
    setSortType(value);
    setSortOpen(false);
  };

  // Keyboard navigation inside list
  const onListKeyDown = (e) => {
    const node = listRef.current;
    if (!node) return;
    const items = Array.from(node.querySelectorAll('[data-opt="1"]'));
    const currentIndex = items.findIndex((el) => el === document.activeElement);
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = items[currentIndex + 1] || items[0];
      next?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = items[currentIndex - 1] || items[items.length - 1];
      prev?.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      const val = document.activeElement?.getAttribute('data-value');
      if (val) applySort(val);
    }
  };

  // Render nothing if closed (hooks above still executed consistently)
  if (!sortOpen) return null;

  return (
    <div
      onClick={() => setSortOpen(false)}
      style={S.backdrop}
      role="presentation"
    >
      <div
        style={{
          ...S.sheet,
          paddingBottom: `calc(12px + env(safe-area-inset-bottom, 0px) + ${isPWA() ? 8 : 0}px)`,
        }}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Sort options"
      >
        {/* Header */}
        <div style={S.header}>
          <div style={S.headerTitle}>Sort By</div>
          <button
            type="button"
            onClick={() => { setSortType('relevance'); setSortOpen(false); }}
            style={S.resetBtn}
          >
            Reset
          </button>
        </div>

        {/* Body (scrollable) */}
        <div
          style={S.body}
          ref={listRef}
          onKeyDown={onListKeyDown}
          role="listbox"
          aria-activedescendant={sortType}
          tabIndex={0}
        >
          {sortOptions.map((opt) => {
            const selected = sortType === opt.value;
            return (
              <button
                key={opt.value}
                data-opt="1"
                data-value={opt.value}
                id={opt.value}
                onClick={() => applySort(opt.value)}
                style={{ ...S.row, ...(selected ? S.rowSelected : null) }}
                aria-selected={selected}
                role="option"
              >
                <span style={S.rowIcon}>
                  {selected ? <FaCheck style={{ fontSize: 12 }} /> : opt.icon}
                </span>
                <span style={S.rowLabel}>{opt.label}</span>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        <div style={S.footer}>
          <button type="button" onClick={() => setSortOpen(false)} style={S.primaryBtn}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
}

/* ───────────────────────── Styles (sheet-only) */
const S = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.40)',
    zIndex: 1000000000001, // on top of everything
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-end',
    padding: 8,
    fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif",
  },
  sheet: {
    width: '100%',
    maxWidth: 500,
    background: '#ffffff',
    borderRadius: 12,
    boxShadow: '0 8px 28px rgba(0,0,0,0.25)',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideUp .25s ease-out',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: 48,
    padding: '0 16px',
    borderBottom: '1px solid #eef0f2',
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: 800,
    color: '#0b0b0b',
    letterSpacing: '-0.01em',
  },
  resetBtn: {
    background: '#f3f4f6',
    border: '1px solid #e5e7eb',
    color: '#374151',
    borderRadius: 999,
    padding: '6px 12px',
    fontSize: 12,
    fontWeight: 700,
    cursor: 'pointer',
  },
  body: {
    maxHeight: 'min(56vh, 520px)',
    overflow: 'auto',
    WebkitOverflowScrolling: 'touch',
    padding: 8,
  },
  row: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: '#ffffff',
    border: '1px solid #eef0f2',
    color: '#0b0b0b',
    borderRadius: 10,
    padding: '10px 12px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'left',
    margin: '6px 0',
  },
  rowSelected: {
    borderColor: '#eeee',
    background: '#eeee',
  },
  rowIcon: {
    width: 18,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  rowLabel: { flex: 1 },
  footer: {
    padding: 12,
    borderTop: '1px solid #eef0f2',
    display: 'flex',
    gap: 8,
  },
  primaryBtn: {
    flex: 1,
    background: '#ddd',
    color: '#000',
    border: '1px solid #dddd',
    borderRadius: 10,
    padding: '12px 14px',
    fontSize: 13,
    fontWeight: 800,
    cursor: 'pointer',
  },
};
